/**
 * We Owe / Due Bill, generated rather than filled.
 *
 * Florida has no state form for this, so the page is drawn from scratch. It is
 * also the only document in the packet that creates an obligation running from
 * the dealership to the buyer rather than the other way round, which is why it
 * is worth getting right: the Buyers Guide the buyer signs says in plain terms
 * that any promise outside it must be in writing. A verbal "we'll sort the
 * alignment out next week" is, on paper, nothing at all.
 *
 * The wording is stored on the deal and printed verbatim. A reprint in
 * November of a page signed in September has to say what was signed in
 * September, so nothing here regenerates the text.
 */

import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from "pdf-lib";
import { usDate, fullName, phone } from "./mappings";
import type { FormContext } from "./mappings";

const INK = rgb(0.07, 0.09, 0.13);
const SOFT = rgb(0.29, 0.34, 0.39);
const RULE = rgb(0.78, 0.80, 0.84);
const NAVY = rgb(0.11, 0.31, 0.85);
const BAND = rgb(0.93, 0.95, 0.98);

const L = 58;
const R = 554;
const W = R - L;

export interface WeOwePromise {
  title: string;
  body: string;
  dueDate: string | null;   // ISO
}

/** Greedy wrap. pdf-lib will not do it, and a clause that runs off the
    right-hand edge is a clause the buyer can say he never read. */
function wrap(text: string, font: PDFFont, size: number, width: number): string[] {
  const out: string[] = [];
  for (const para of text.split("\n")) {
    if (!para.trim()) { out.push(""); continue; }
    let line = "";
    for (const word of para.trim().split(/\s+/)) {
      const trial = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(trial, size) <= width) {
        line = trial;
      } else {
        if (line) out.push(line);
        line = word;
      }
    }
    if (line) out.push(line);
  }
  return out;
}

/**
 * Strip any leading "1." / "2)" the drafter produced. The page numbers the
 * clauses itself, and two numbering schemes on one document is how you get
 * "item 3" meaning two different things in the same argument.
 */
function clauses(body: string): string[] {
  return body
    .split(/\n\s*\n/)
    .map((b) => b.trim().replace(/^\s*\d+\s*[.)]\s*/, "").replace(/\s*\n\s*/g, " "))
    .filter(Boolean);
}

export async function buildWeOwe(
  ctx: FormContext,
  promises: WeOwePromise[],
): Promise<Uint8Array> {
  if (promises.length === 0) {
    throw new Error("There is nothing owed on this deal to print.");
  }

  const doc = await PDFDocument.create();
  const reg = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);

  const { dealer, buyer, coBuyer, vehicle, deal } = ctx;
  const dealerName = (dealer.dba ?? dealer.legalName).toUpperCase();
  const dealerLegal = dealer.dba ?? dealer.legalName;

  let page: PDFPage = doc.addPage([612, 792]);
  let y = 742;

  const at = (t: string, x: number, size = 10, f = reg, color = INK) =>
    page.drawText(t, { x, y, size, font: f, color });
  const right = (t: string, xr: number, size = 10, f = reg, color = INK) =>
    page.drawText(t, { x: xr - f.widthOfTextAtSize(t, size), y, size, font: f, color });
  const rule = (yy: number, from = L, to = R, c = RULE, thickness = 0.75) =>
    page.drawLine({ start: { x: from, y: yy }, end: { x: to, y: yy }, thickness, color: c });

  /* A long list of promises can run past one page. Rather than silently
     clipping — which would drop a commitment off a signed document — the page
     breaks and the footer is repeated. */
  const footerAt = (p: PDFPage, n: number, of: number) => {
    p.drawLine({ start: { x: L, y: 62 }, end: { x: R, y: 62 }, thickness: 0.75, color: RULE });
    p.drawText(`${dealerName}  ·  FL Dealer License ${dealer.flhsmvLicenseNo}`,
      { x: L, y: 48, size: 7.5, font: reg, color: SOFT });
    const tail = `Deal ${deal.dealNo}  ·  Page ${n} of ${of}`;
    p.drawText(tail,
      { x: R - reg.widthOfTextAtSize(tail, 7.5), y: 48, size: 7.5, font: reg, color: SOFT });
  };

  /* The lowest a baseline may sit before it collides with the footer rule at
     62. Named, because a guessed number here is how a signature block ends up
     alone on a second page with the terms it belongs to left behind. */
  const FLOOR = 78;

  const pages: PDFPage[] = [page];

  /* Break to a fresh page when what comes next will not fit. Every new page
     is stamped with what is being signed: a loose second sheet with clauses
     and a signature line but no deal number on it is worth very little when
     somebody produces it three months later. */
  const room = (needed: number): boolean => {
    if (y - needed >= FLOOR) return false;
    page = doc.addPage([612, 792]);
    pages.push(page);
    y = 742;
    at(`We Owe / Due Bill — deal ${deal.dealNo}, continued`, L, 9, reg, SOFT);
    y -= 10;
    rule(y);
    y -= 22;
    return true;
  };

  // ---- letterhead --------------------------------------------------------
  at(dealerName, L, 16, bold);
  let ry = y + 4;
  const head = [
    dealer.street,
    `${dealer.city}, ${dealer.state} ${dealer.zip}`,
    phone(dealer.phone),
    dealer.email ?? "",
  ].filter(Boolean);
  for (const line of head) {
    page.drawText(line, {
      x: R - reg.widthOfTextAtSize(line, 8.5), y: ry, size: 8.5, font: reg, color: SOFT,
    });
    ry -= 10.5;
  }
  y -= 15;
  at(`FL Dealer License ${dealer.flhsmvLicenseNo}`, L, 8.5, reg, SOFT);
  y -= 22;
  rule(y, L, R, NAVY, 2);
  y -= 26;

  // ---- title -------------------------------------------------------------
  at("WE OWE / DUE BILL", L, 19, bold);
  right(`Deal ${deal.dealNo}   ·   ${usDate(deal.agreementDate)}`, R, 9.5, reg, SOFT);
  y -= 15;
  at("Written commitment to perform repairs after delivery", L, 9.5, reg, SOFT);
  y -= 22;

  // ---- buyer and vehicle, side by side ----------------------------------
  const owner = buyer.isBusiness && buyer.businessName ? buyer.businessName : fullName(buyer);
  const buyerRows: [string, string][] = [
    ["Name", owner],
    ["Address", buyer.street],
    ["", `${buyer.city}, ${buyer.state} ${buyer.zip}`],
    ["Phone", phone(buyer.phoneCell)],
    ["Email", buyer.email ?? "—"],
  ];
  if (coBuyer) buyerRows.splice(1, 0, ["Co-buyer", fullName(coBuyer)]);

  const vehicleRows: [string, string][] = [
    ["Vehicle", `${vehicle.year} ${vehicle.make} ${vehicle.model}`],
    ["VIN", vehicle.vin],
    ["Stock", ctx.stockNo ?? "—"],
    ["Odometer", vehicle.odometer != null
      ? `${vehicle.odometer.toLocaleString("en-US")} miles` : "—"],
    ["Sold", usDate(deal.agreementDate)],
  ];

  const col = (W - 25) / 2;
  const x2 = L + col + 25;
  const headerH = 17;
  const rows = Math.max(buyerRows.length, vehicleRows.length);
  const bodyH = rows * 14 + 8;
  const top = y;

  for (const x of [L, x2]) {
    page.drawRectangle({ x, y: top - headerH, width: col, height: headerH, color: BAND });
    page.drawRectangle({
      x, y: top - headerH - bodyH, width: col, height: headerH + bodyH,
      borderColor: RULE, borderWidth: 0.75,
    });
  }
  page.drawText("BUYER", { x: L + 7, y: top - 12, size: 8.5, font: bold, color: NAVY });
  page.drawText("VEHICLE", { x: x2 + 7, y: top - 12, size: 8.5, font: bold, color: NAVY });

  for (const [x, rws] of [[L, buyerRows], [x2, vehicleRows]] as [number, [string, string][]][]) {
    let ly = top - headerH - 13;
    for (const [label, value] of rws) {
      if (label) {
        page.drawText(label, { x: x + 7, y: ly, size: 8, font: reg, color: SOFT });
      }
      const strong = label === "Name" || label === "Vehicle";
      page.drawText(value, {
        x: x + 62, y: ly, size: 9, font: strong ? bold : reg, color: INK,
      });
      ly -= 14;
    }
  }
  y = top - headerH - bodyH - 22;

  // ---- preamble ----------------------------------------------------------
  const preamble =
    `${dealerLegal} agrees to perform the following work on the vehicle described `
    + "above, at no cost to the buyer, as part of the sale of that vehicle. This page "
    + `is attached to and forms part of the purchase agreement for deal ${deal.dealNo}.`;
  for (const line of wrap(preamble, reg, 10, W)) {
    at(line, L, 10);
    y -= 14.5;
  }
  y -= 14;

  at("What we owe", L, 10.5, bold);
  y -= 18;

  // ---- the promises ------------------------------------------------------
  let n = 0;
  for (const promise of promises) {
    for (const clause of clauses(promise.body)) {
      n += 1;
      const lines = wrap(clause, reg, 10, W - 18);
      room(lines.length * 14.5 + 10);
      at(`${n}.`, L, 10, bold, NAVY);
      for (const line of lines) {
        at(line, L + 18, 10);
        y -= 14.5;
      }
      y -= 7;
    }
  }

  // ---- what this does not change ----------------------------------------
  const warrantyNote = (ctx.warrantyDays ?? 0) > 0
    ? `This commitment is in addition to the ${Math.round((ctx.warrantyDays ?? 0) / 30)} month `
      + `/ ${(ctx.warrantyMiles ?? 0).toLocaleString("en-US")} mile powertrain warranty `
      + "included with this vehicle, and does not replace, shorten or limit it. "
    : "";
  const closing = warrantyNote
    + "Nothing on this page is conditioned on the buyer paying anything further.";

  const closingLines = wrap(closing, reg, 9.5, W);
  const brokeForClosing = room(4 + 14 + closingLines.length * 13.5 + 18);
  if (!brokeForClosing) {
    y -= 4;
    rule(y);
    y -= 14;
  }
  for (const line of closingLines) {
    at(line, L, 9.5);
    y -= 13.5;
  }
  y -= 18;

  // ---- signatures --------------------------------------------------------
  /* Measured rather than guessed. From the heading down to the lowest printed
     name is 74pt for one row and another 64 for a co-buyer's, and the whole
     block moves together: a signature page that arrives without the terms
     above it is the one thing this document must never do. */
  const SIG_BLOCK = 12 + 38 + 24 + (coBuyer ? 64 : 0);
  room(SIG_BLOCK);

  at("Signatures", L, 10.5, bold);
  y -= 12;
  at("Both parties keep a signed copy. The buyer's copy is theirs to hold until the "
     + "work is done.", L, 8.5, reg, SOFT);
  y -= 38;

  /** One signature line with a short date rule beside it. Passing null for the
      right-hand side leaves that half blank rather than drawing an empty rule
      nobody is meant to sign. */
  const sigRow = (
    left: { label: string; name: string },
    rightSide: { label: string; name: string } | null,
  ) => {
    const sigCol = (W - 36) / 2;
    const rx = L + sigCol + 36;
    const sigW = sigCol - 83;
    const labelY = y - 10;
    const nameY = y - 24;

    const half = (x: number, side: { label: string; name: string }) => {
      rule(y, x, x + sigW, INK, 0.8);
      rule(y, x + sigW + 14, x + sigCol, INK, 0.8);
      page.drawText(side.label, { x, y: labelY, size: 7.5, font: reg, color: SOFT });
      page.drawText("Date", { x: x + sigW + 14, y: labelY, size: 7.5, font: reg, color: SOFT });
      page.drawText(side.name, { x, y: nameY, size: 9, font: reg, color: INK });
    };

    half(L, left);
    if (rightSide) half(rx, rightSide);
    y -= 40;
  };

  sigRow(
    { label: "Buyer signature", name: fullName(buyer) },
    { label: "Dealer representative", name: dealerLegal },
  );
  // No room() here: SIG_BLOCK already reserved space for this row, and
  // breaking between the two buyers' signatures would split the block.
  if (coBuyer) {
    sigRow({ label: "Co-buyer signature", name: fullName(coBuyer) }, null);
  }

  pages.forEach((p, i) => footerAt(p, i + 1, pages.length));

  doc.setTitle(`${deal.dealNo} we owe`);
  doc.setSubject("Written repair commitment");
  doc.setProducer("FrontLot");
  return doc.save();
}
